// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * API endpoint for removing event groups from assets.
 * Provides functionality to delete multi-step processes like shipping
 * or maintenance routines when they are no longer needed or were
 * created in error.
 */
import * as z from 'zod';
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';

/**
 * Schema for validating the API response when deleting an event group.
 * Transforms the response to provide a more intuitive structure with
 * the success flag separated from metadata. The success flag indicates
 * whether the deletion was completed successfully.
 */
const deleteEventGroupResponseSchema = z
  .object({
    data: z.object({ success: z.boolean() }),
    metadata: responseMetadataSchema,
  })
  .transform(({ data, ...response }) => ({
    ...response,
    success: data.success,
  }));

type DeleteEventGroupResponse = z.infer<typeof deleteEventGroupResponseSchema>;

/**
 * Deletes an event group and all its associated events.
 * This function handles the API call to remove a multi-step process
 * and validates the response data using Zod schemas. It permanently
 * removes the event group and all its events from the asset's history.
 *
 * @param assetKey - Unique identifier of the asset containing the event group
 * @param eventGroupKey - Unique identifier of the event group to delete
 * @returns Promise resolving to the deletion result and request metadata
 * @throws Will throw an error if the API request fails or if the response data is invalid
 */
export async function deleteEventGroup(
  assetKey: string,
  eventGroupKey: string
): Promise<DeleteEventGroupResponse> {
  const url = `/v1/asset/${assetKey}/event-group/${eventGroupKey}`;
  const response = await api.delete(url);

  return parseResponse(deleteEventGroupResponseSchema, response.data, {
    schemaName: 'DeleteEventGroup',
    endpoint: url,
  });
}
